import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, CheckCircle, House } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Pagina pubblica recensione agente — l'utente arriva qui da un link email
 * con token unico. Lascia stelle (1-5) + testo opzionale → la recensione
 * viene salvata. Token monouso.
 */
export default function PublicReview() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Lascia una recensione · Pronto";
    fetch(`${API}/public/review/${token}`)
      .then((r) => {
        if (!r.ok) {
          if (r.status === 410) {
            setError("Hai già lasciato la recensione, grazie!");
          } else if (r.status === 404) {
            setError("Questo link non è valido o è scaduto.");
          } else {
            setError("Errore di caricamento.");
          }
          return null;
        }
        return r.json();
      })
      .then((data) => data && setInfo(data))
      .finally(() => setLoading(false));
  }, [token]);

  const onSubmit = async () => {
    if (stars < 1 || stars > 5) {
      toast.error("Scegli da 1 a 5 stelle.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, stars, text: text.trim() || undefined }),
      });
      if (!r.ok) throw new Error();
      setDone(true);
    } catch (err) {
      toast.error("Invio non riuscito. Riprova fra un istante.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-3 border-[#7C8B6E] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-5">
        <div className="bg-white border border-[#EAE7DF] rounded-2xl p-8 max-w-md text-center">
          <House size={32} weight="duotone" className="text-[#C8C5BD] mx-auto mb-3" />
          <h2 className="font-editorial text-[22px] text-[#1C1C1A]">{error}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#1C1C1A]" data-testid="review-page">
      <main className="max-w-2xl mx-auto px-5 py-10 sm:py-16">
        {!done ? (
          <>
            <h1 className="font-editorial text-[26px] sm:text-[34px] leading-tight">
              Com'è andata con<br />
              <span className="text-[#7C8B6E] italic">{info?.agent?.name}?</span>
            </h1>
            <p className="mt-3 text-[14px] text-[#3F3D38] leading-relaxed">
              Aiuta gli altri proprietari di {info?.city || "casa"} a scegliere. La tua recensione resta pubblica sul profilo dell'agente.
            </p>

            <div className="mt-8 bg-white border border-[#EAE7DF] rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-3 pb-4 border-b border-[#EAE7DF]">
                {info?.agent?.picture && (
                  <img src={info.agent.picture} alt={info.agent.name} className="w-12 h-12 rounded-full object-cover" />
                )}
                <div>
                  <div className="font-editorial text-[17px] text-[#1C1C1A]">{info?.agent?.name}</div>
                  {info?.agent?.agency_name && (
                    <div className="text-[12px] text-[#6B6A65]">{info.agent.agency_name}</div>
                  )}
                </div>
              </div>

              {/* Stelle */}
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65] mb-2.5">
                  La tua valutazione
                </div>
                <div className="flex items-center gap-1.5">
                  {[1,2,3,4,5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => setStars(n)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`review-star-${n}`}
                      aria-label={`${n} stelle`}
                    >
                      <Star
                        size={36}
                        weight={(hover || stars) >= n ? "fill" : "regular"}
                        className={(hover || stars) >= n ? "text-[#D97706]" : "text-[#C8C5BD]"}
                      />
                    </button>
                  ))}
                </div>
                {stars > 0 && (
                  <div className="mt-2 text-[12.5px] text-[#6B6A65]">
                    {["Pessimo","Da migliorare","Discreto","Buono","Eccellente"][stars - 1]}
                  </div>
                )}
              </div>

              {/* Testo */}
              <div className="mt-6">
                <label className="block text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65] mb-1.5">
                  Racconta in 2 parole (opzionale)
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Es. Risposta veloce, valutazione precisa, modi gentili. Cosa ti è piaciuto?"
                  rows={4}
                  maxLength={800}
                  className="w-full px-3.5 py-2.5 text-[14px] border border-[#EAE7DF] rounded-md focus:outline-none focus:border-[#7C8B6E] focus:ring-2 focus:ring-[#7C8B6E]/20 bg-white"
                  data-testid="review-text"
                />
                <div className="text-[11px] text-[#9A968B] mt-1 text-right">{text.length}/800</div>
              </div>

              <button
                onClick={onSubmit}
                disabled={submitting || stars === 0}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#1C1C1A] hover:bg-black text-white text-[14px] font-semibold transition-colors disabled:opacity-50"
                data-testid="review-submit"
              >
                {submitting ? "Invio in corso…" : "Pubblica recensione"}
              </button>
            </div>
          </>
        ) : (
          <div className="bg-white border border-[#7C8B6E]/30 rounded-2xl p-8 sm:p-10 text-center" data-testid="review-done">
            <CheckCircle size={40} weight="duotone" className="text-[#7C8B6E] mx-auto" />
            <h2 className="font-editorial text-[24px] mt-3 text-[#1C1C1A]">Grazie {info?.lead_name?.split(" ")[0] || ""}!</h2>
            <p className="mt-2 text-[14px] text-[#3F3D38] leading-relaxed max-w-md mx-auto">
              {stars >= 4
                ? "La tua recensione è già visibile sul profilo dell'agente."
                : "La tua recensione sarà visibile dopo una breve verifica. Grazie per il feedback onesto — aiuta a migliorare il servizio."}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
